import { useEffect, useState } from "react";
import "./Upload.css";
import { Trash2, Upload, UploadCloud } from "lucide-react";
import Loading from "../../../../components/Loading";
import Hamburger from "../../../../components/Hamburger";
import { ReactComponent as GNAiIcon } from "../../../../icons/gnai_logo.svg";
import { GridcoTitle } from "../../components";
import { uploadGridcoNetScheduleFile } from "../../../../Rest_api/RestApi";

const FileRegexPatterns: { [key: string]: RegExp } = {
  AVT: /^AVT \d{2}\.\d{2}\.\d{4} ?\.xlsx$/,
  "Day Ahead Schedule for MTOA":
    /^Day Ahead Schedule for MTOA\(Jun-25\)( \(1\))?\.xlsx$/,
  "Declared Capacity of GKEL":
    /^Declared Capacity of GKEL for \d{2}\.\d{2}\.\d{4} \.xls$/,
  "IEX DOR GRIDCO": /^IEX\d{6}DOR_GDC0001_OR0_GRIDCO\.xls$/,
  "JSW Energy Utkal Schedule":
    /^JSW Energy Utkal Schedule & GRIDCO allocation for \d{2}\.\d{2}\.\d{2}\.xlsx$/,
  "NetSchdReportBreakup ISGS":
    /^NetSchdReportBreakup@ODISHA_STATE@ISGS@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "NetSchdReportBreakup OA_GNA":
    /^NetSchdReportBreakup@ODISHA_STATE@OA_GNA@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "NetSchdReportBreakup OA_REMC":
    /^NetSchdReportBreakup@ODISHA_STATE@OA_REMC@rev\(\d+\)@\d{2}-\d{2}-\d{4}@\d{2}-\d{2}-\d{4}_\d{2}-\d{2}-\d{2}\.xlsx$/,
  "OPGC STAGE-2 DC": /^OPGC STAGE-2 DC \d{2}\.\d{2}\.\d{4} - R0\.xls$/,
  "RTM_IEX DOR GRIDCO": /^RTM_IEX\d{6}DOR_GDC0001_OR0_GRIDCO(\s?\(1\))?\.XLS$/i,
  "SLDC LDS":
    /^SLDC LDS \d{2} (Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?) \d{4} \(Rev-\d+\)\.xlsx$/i,
  "NBVL(IPP) DAY AHEAD SCHEDULE":
    /^NBVL\(IPP\) DAY AHEAD SCHEDULE TO GRIDCO FOR \d{2}\.\d{2}\.\d{4}\.xlsx$/,
  MAADURGA: /^DC_INJECTION_\d{2}\.\d{2}\.\d{4}\.xlsx$/,
  jipl_dc_pdf: /^JIPL DC for DD \d{2}\.\d{2}\.\d{4}\.pdf$/,
  sldc_report: /^SLDC_Report__\d{8}\.xlsx$/,
};

const RegexToFileTypeMap: { [key: string]: string } = {
  AVT: "OPGC-1 (IBTPS Unit -1 & 2)",
  "OPGC STAGE-2 DC": "OPGC-2 (IBTPS Unit -3 & 4)",
  "SLDC LDS": "Vedanta",
  "Declared Capacity of GKEL": "GMR",
  "JSW Energy Utkal Schedule": "JSWEUL",
  "NBVL(IPP) DAY AHEAD SCHEDULE": "NBVL",
  "NetSchdReportBreakup ISGS": "Net Schedule ISGS",
  "NetSchdReportBreakup OA_GNA": "Net Schedule OA GNA",
  "NetSchdReportBreakup OA_REMC": "Net Schedule OA REMC",
  "RTM_IEX DOR GRIDCO": "RTM day current",
  "IEX DOR GRIDCO": "DAM day current",
  MAADURGA: "Maa Durga",
  jipl_dc_pdf: "JIPL",
  sldc_report: "State Hydro Generation",
};

const AutomationFileTypes = [
  "Net Schedule ISGS",
  "OPGC-1 (IBTPS Unit -1 & 2)",
  "OPGC-2 (IBTPS Unit -3 & 4)",
  "Vedanta",
  "GMR",
  "JIPL",
  "JSWEUL",
  "NBVL",
  "Maa Durga",
  "Net Schedule OA REMC",
  "Net Schedule OA GNA",
  "State Hydro Generation",
  "RTM day before",
  "RTM day current",
  "DAM day current",
];

type UploadedFile = {
  type: string;
  date: string;
  file_url: string;
  file: File;
  id: number;
};

const hyperlinkColumns: { [key: string]: string } = { file_url: "file_url" };

const FileTypeBackendAlias: { [key: string]: string } = {
  "Net Schedule ISGS": "isgs",
  "OPGC-1 (IBTPS Unit -1 & 2)": "opgc-1",
  "OPGC-2 (IBTPS Unit -3 & 4)": "opgc-2",
  Vedanta: "vedanta",
  GMR: "gmr",
  JIPL: "jipl",
  JSWEUL: "jsweul",
  NBVL: "nbvl",
  "Maa Durga": "maa-durga",
  "Net Schedule OA REMC": "oa-remc",
  "Net Schedule OA GNA": "oa-gna",
  "State Hydro Generation": "state-hydro",
  "RTM day before": "rtm-day-before",
  "RTM day current": "rtm-day-current",
  "DAM day current": "dam-day-current",
};

export default function GridcoNetSchedule() {
  const [fileTypeList, setFileTypeList] =
    useState<string[]>(AutomationFileTypes);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedFileType, setSelectedFileType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  console.log("🚀 ~ GridcoNetSchedule ~ uploadedFiles:", uploadedFiles);
  const columnNames = ["Type", "Date", "File Link", "Delete"];
  const columnKeys = ["type", "date", "file_url", "id"];

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      let updatedfileTypes = [...AutomationFileTypes];
      for (let index = 0; index < uploadedFiles.length; index++) {
        let fileType = uploadedFiles[index].type;

        if (updatedfileTypes.includes(fileType)) {
          updatedfileTypes = updatedfileTypes.filter(
            (type) => type !== fileType
          );
        }
      }
      setFileTypeList(updatedfileTypes);
    } else {
      setFileTypeList([...AutomationFileTypes]);
    }
  }, [uploadedFiles]);

  function detectFileType(fileName: string): string {
    for (const [regexKey, pattern] of Object.entries(FileRegexPatterns)) {
      if (pattern.test(fileName)) {
        return RegexToFileTypeMap[regexKey] || regexKey;
      }
    }
    return ""; // Return empty if no match found
  }

  function handleBulkUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;

    if (!selectedDate) {
      alert("Please select a date");
      event.target.value = "";
      return;
    }

    if (!files || files.length === 0) {
      alert("Please select files");
      return;
    }

    const validExtensions = [".xlsx", ".xls", ".pdf"];
    const newFiles: UploadedFile[] = [];
    const undetectedFiles: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();

      if (!validExtensions.includes(fileExtension)) {
        alert(
          `Invalid file format for ${file.name}. Please select only XLSX, XLS or PDF files.`
        );
        event.target.value = "";
        return;
      }

      // Auto-detect file type using regex
      const detectedType = detectFileType(file.name);

      if (!detectedType) {
        undetectedFiles.push(file.name);
        continue;
      }

      // const isAlreadyUploaded = uploadedFiles.some(
      //   (uf) => uf.type === detectedType
      // );
      // if (isAlreadyUploaded) {
      //   alert(
      //     `File type "${detectedType}" is already uploaded. Skipping ${file.name}.`
      //   );
      //   continue;
      // }

      const newFile: UploadedFile = {
        type: detectedType,
        date: selectedDate,
        file_url: URL.createObjectURL(file),
        file: file,
        id: uploadedFiles.length + newFiles.length + 1,
      };

      newFiles.push(newFile);
    }

    if (undetectedFiles.length > 0) {
      alert(
        `Could not auto-detect file type for:\n${undetectedFiles.join(
          "\n"
        )}\n\nPlease upload these files individually.`
      );
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prevFiles) => [...newFiles, ...prevFiles]);
    }

    event.target.value = "";
  }

  function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];

    if (!selectedDate) {
      alert("Please select a date");
      event.target.value = "";
      return;
    }

    if (!selectedFileType) {
      alert("Please select a file type");
      event.target.value = "";
      return;
    }

    if (!file) {
      alert("Please select a file");
      return;
    }

    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    const validExtensions = [".xlsx", ".pdf"];

    if (!validExtensions.includes(fileExtension)) {
      alert(`Invalid file format. Please select only XLSX or PDF files.`);
      event.target.value = "";
      return;
    }

    const newFile = {
      type: selectedFileType,
      date: selectedDate,
      file_url: URL.createObjectURL(file),
      file: file,
      file_name: file.name,
      id: uploadedFiles.length + 1,
    };

    setUploadedFiles((prevFiles) => [newFile, ...prevFiles]);
    setSelectedFileType("");
    event.target.value = "";
  }

  const renderTableCell = (item: any, columnKey: string) => {
    if (columnKey === "id") {
      // If the column is meant to display a delete button
      return (
        <div className="flex justify-center">
          <button
            className="text-red-500"
            onClick={() => {
              setUploadedFiles((prevFiles) =>
                prevFiles.filter((file) => file.id !== item.id)
              );
            }}
          >
            <Trash2 size={18} />
          </button>
        </div>
      );
    }
    if (hyperlinkColumns[columnKey]) {
      // If the column is meant to display a hyperlink
      const url = item[hyperlinkColumns[columnKey]]; // Get the URL from the data
      return (
        <div className="">
          <a
            href={url}
            className="text-primary font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            View File
          </a>
        </div>
      );
    }

    // Default behavior for non-hyperlink cells
    return (
      <div className="text-[#8C8A96] text-[12px]">
        {typeof item[columnKey] === "number"
          ? item[columnKey] % 1 === 0
            ? item[columnKey]
            : item[columnKey].toFixed(2)
          : item[columnKey]}
      </div>
    );
  };

  async function handleGenerate() {
    if (uploadedFiles.length === 0) {
      alert("Please upload a file");
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();

      // Build metadata and append files
      const metaData = uploadedFiles.map((file, idx) => {
        const field = `file_${idx}`;
        formData.append(field, file.file);
        return {
          field, // reference to the file field
          date: file.date,
          type: FileTypeBackendAlias[file.type] || file.type,
        };
      });
      console.log("🚀 ~ GridcoNetSchedule ~ metaData:", metaData);

      return;

      // Attach metadata as a single JSON string
      // formData.append("metaData", JSON.stringify(metaData));

      // const res = await uploadGridcoNetScheduleFile(formData);

      // setUploadedFiles([]);
      // setSelectedFileType("");
      // setSelectedDate("");

      // // Check if response contains a file URL
      // if (!res.automation_files || !res.automation_files[0]?.file_url) {
      //   alert("No link found in the response");
      //   return;
      // }

      // // Get the file URL from response
      // const fileUrl = res.automation_files[0].file_url;

      // // First open a new tab, then trigger the download
      // const newTab = window.open("about:blank", "_blank");
      // if (newTab) {
      //   // Once the new tab is ready, navigate to the file URL
      //   setTimeout(() => {
      //     newTab.location.href = fileUrl;
      //   }, 100);
      //   alert(
      //     "File generated successfully! The download will begin in the new tab."
      //   );
      // } else {
      //   // If popup blocked, provide direct link
      //   alert(
      //     "File generated successfully! Your browser may have blocked the popup. Click OK to download directly."
      //   );
      //   window.location.href = fileUrl;
      // }
    } catch (error) {
      console.error("Error generating file:", error);
      alert("Error generating file. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full h-full ">
      <div className="header grid grid-cols-3 gap-5 items-center justify-between">
        {" "}
        <div className="col-span-1 flex items-center gap-5">
          <GNAiIcon width={80} />
          <Hamburger />
        </div>
        <div className="flex-1 flex justify-center">
          <GridcoTitle />
        </div>
      </div>
      <div className="tabContent">

      <div className="m-4" style={{ height: "unset!important" }}>
        {loading && <Loading />}

        <div className="flex justify-between items-center  bg-white p-3 rounded-lg w-full mb-4">
          <div id="raj-upload" className=" flex gap-10 items-center">
            <div className="flex gap-3 items-center">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                1
              </div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border border-[#C5C5CBD9] rounded-lg p-2 text-[#8C8A96]"
              />
            </div>

            <div className="flex gap-3 items-center">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                2
              </div>

              <select
                className="border border-[#C5C5CBD9] rounded-lg p-2 max-w-[200px] text-[#8C8A96] "
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
              >
                <option value="" disabled>
                  Select File Type
                </option>
                {fileTypeList.map((fileType, index) => (
                  <option key={index} value={fileType}>
                    {fileType}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 items-center">
              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white text-xs font-semibold shadow-sm">
                3
              </div>

              <div className="">
                <label className="downloadButton cursor-pointer font-medium flex items-center gap-3 h-[38px]">
                  Upload File <Upload size={20} />
                  <input
                    required={true}
                    type="file"
                    accept=".xlsx, .pdf"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      handleFileUpload(e);
                    }}
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="">
            <label className="cursor-pointer flex items-center gap-2 h-[38px] bg-primary text-white px-3 rounded-full transition-colors">
              <UploadCloud size={18} />
              <span className="text-xs font-medium">Bulk Upload</span>
              <input
                type="file"
                accept=".xlsx, .xls, .pdf"
                multiple
                style={{ display: "none" }}
                onChange={(e) => {
                  handleBulkUpload(e);
                }}
              />
            </label>
          </div>
        </div>

        <div className="table-container bg-white rounded-xl">
          {/* Table Header */}
          <div className="table-contentHeading w-full">
            {columnNames.map((columnName, index) => (
              <div key={index} className="column-heading w-full">
                {columnName}
              </div>
            ))}
          </div>

          {/* Table Rows */}
          <div className="elemDetailsSec">
            {uploadedFiles.length > 0 ? (
              uploadedFiles.map((item, index) => (
                <div className="tableDetails border-b" key={index}>
                  {columnKeys.map((columnKey, index) => (
                    <div key={index} className="table-cell w-full text-center">
                      {renderTableCell(item, columnKey)}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="py-8 text-center text-gray-500">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg p-2 m-4 flex items-center justify-end">
        <button
          onClick={handleGenerate}
          disabled={uploadedFiles.length === 0}
          className="p-2 px-4 disabled:cursor-not-allowed disabled:bg-[#c4c4c4] text-white rounded-lg bg-[#F1935C] hover:shadow-lg transition-all duration-300 ease-in-out"
        >
          Generate
        </button>
      </div>

      <div className="gna-footer-container">
        <div className="grid grid-cols-2 justify-between gap-5 ">
          <p className="gna-footer-text">
            Powered by <strong>GNA Energy</strong>
          </p>
          <strong className="gna-footer-text  text-end">Beta Version</strong>
        </div>
      </div>
    </div>
    </div>
  );
}
